import type { ComputedRef, Ref } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { User, UserFormValues } from './interfaces'

import { useSidebarPrivileges } from '@entities/viewer'
import { AppRoute } from '@shared/config'

import { useUsersStore } from './store'
import { useUserDelete } from '../api/delete.hook'
import { useUserSave } from '../api/save.hook'
import { useUserApi } from '../api/show.hook'
import { useUserOptions } from './user-options'

interface UserEditReturn {
  refName: Ref<string>
  refEmail: Ref<string>
  refStatus: Ref<string | undefined>
  refRoles: Ref<string[]>
  refTeams: Ref<number[]>
  refSlack: Ref<string>
  roleOptions: ComputedRef<AppSelectOption[]>
  statusOptions: ComputedRef<AppSelectOption[]>
  teamOptions: ComputedRef<AppSelectOption<number>[]>
  user: ComputedRef<User | null>
  isSaving: ComputedRef<boolean>
  isNotFound: Ref<boolean>
  isSaveDisabled: ComputedRef<boolean>
  canManage: ComputedRef<boolean>
  isDeleteOpen: Ref<boolean>
  load: () => Promise<void>
  save: () => Promise<void>
  askDelete: () => void
  cancelDelete: () => void
  confirmDelete: () => Promise<void>
}

export const useUserEditPage = (): UserEditReturn => {
  const route = useRoute()
  const router = useRouter()
  const store = useUsersStore()
  const { roleOptions, statusOptions } = useUserOptions()
  const { canManageUsers } = useSidebarPrivileges()

  const userId = String(route.params.id)

  const refName = ref<string>('')
  const refEmail = ref<string>('')
  const refStatus = ref<string | undefined>(undefined)
  const refRoles = ref<string[]>([])
  const refTeams = ref<number[]>([])
  const refSlack = ref<string>('')
  const isNotFound = ref<boolean>(false)
  const isDeleteOpen = ref<boolean>(false)

  /** Snapshot of the last persisted values — the Save button stays disabled until something differs. */
  const original = ref<string>('')

  const user = computed<User | null>(() => store.getCurrentUser)
  const isSaving = computed<boolean>(() => store.isSaving)
  const canManage = computed<boolean>(() => canManageUsers.value)
  const teamOptions = computed<AppSelectOption<number>[]>(() =>
    store.getTeams.map(team => ({ value: team.id, text: team.name })))

  const snapshot = (): string => JSON.stringify({
    name: refName.value.trim(),
    email: refEmail.value.trim(),
    status: refStatus.value ?? '',
    roles: [...refRoles.value].sort(),
    teams: [...refTeams.value].sort((a, b) => a - b),
    slack: refSlack.value.trim(),
  })

  const isSaveDisabled = computed<boolean>(() =>
    !canManage.value || !refName.value.trim() || snapshot() === original.value)

  const applyUser = (loaded: User): void => {
    refName.value = loaded.name ?? ''
    refEmail.value = loaded.email ?? ''
    refStatus.value = loaded.status || undefined
    refRoles.value = [...(loaded.roles ?? [])]
    refTeams.value = (loaded.teams ?? []).map(team => team.id)
    refSlack.value = loaded.slack ?? ''
    original.value = snapshot()
  }

  const load = async (): Promise<void> => {
    isNotFound.value = false
    // Clear any user left from a previous visit so the form stays gated (and hidden behind the
    // global loader) until this user's data arrives — no empty-form validation flash.
    store.setCurrentUser(null)

    const result = await useUserApi(userId)

    if (!result) {
      isNotFound.value = true
      return
    }

    applyUser(result.user)
  }

  const save = async (): Promise<void> => {
    const payload: UserFormValues = {
      name: refName.value.trim(),
      email: refEmail.value.trim(),
      status: refStatus.value ?? '',
      roles: refRoles.value,
      teams: refTeams.value,
      slack: refSlack.value.trim(),
    }

    const updated = await useUserSave({ userId, payload })

    if (updated) {
      store.setCurrentUser(updated)
      applyUser(updated)
    }
  }

  const askDelete = (): void => {
    isDeleteOpen.value = true
  }

  const cancelDelete = (): void => {
    isDeleteOpen.value = false
  }

  const confirmDelete = async (): Promise<void> => {
    const ok = await useUserDelete(userId)

    if (ok) {
      isDeleteOpen.value = false
      await router.push(AppRoute.Users)
    }
  }

  return {
    refName,
    refEmail,
    refStatus,
    refRoles,
    refTeams,
    refSlack,
    roleOptions,
    statusOptions,
    teamOptions,
    user,
    isSaving,
    isNotFound,
    isSaveDisabled,
    canManage,
    isDeleteOpen,
    load,
    save,
    askDelete,
    cancelDelete,
    confirmDelete,
  }
}
